import * as React from 'react'
import { Text, View, FlatList, TouchableOpacity, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Icon from '@components/icon/Icons'
import Button from '@components/button/Button'
import SectionTitle from '@components/sectionTitle/SectionTitle'
import { Service } from '@components/modal/ModalSearchSection'
import Drawer from '@components/drawer/Drawer'
import Steps from '@components/steps/Steps'
import SimpleAlert from '@components/modal/SimpleAlert'
import { ModalListService } from '@components/modal/ModalList'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as EDLStore from '@store/edl/edl'
import * as SiteStore from '@store/site/site'
import * as IncidentStore from '@store/incident/incident'
import * as LocationStore from '@store/location/location'
import * as UserStore from '@store/user/user'
import * as PlanStore from '@store/plan/plan'
import { ensureCached } from '@utils/map/cacheMap'
import useSyncStore from '@store/useSyncStore'

import api from '@api/api'
import { EtatDesLieuxTypeEnum } from '@api/enum'

import { allStepsEDL, findTenant, findManifestation, filterIncidentsBySpace } from '../utils'
import { manifestationDate } from '@screens/manifestations/manifestationsUtils'
import { getTenantDates, getTenantStatus } from '@screens/tenants/tenantsUtils'
import { findLocalisationSubLevels } from '@screens/incident/utils'

import Logger from '@utils/logger'

interface Props {
  id: string
  type: EtatDesLieuxTypeEnum
  origin: 'manifestation' | 'edl'
  manifestationId?: string
  tenantId?: string
}

type INSection = 'in' | 'out'

const EDLSpaceScreen = ({ id, type, manifestationId, tenantId, origin }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const [, , actions, online] = useSyncStore()

  const form = useReducer(EDLStore.store, (s) => s.form)
  const forms = useReducer(EDLStore.store, (s) => s.forms)
  const EDLs = useReducer(EDLStore.store, (s) => s.EDLs)

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const tenants = useReducer(SiteStore.store, (s) => s.tenants)
  const user = useReducer(UserStore.store, (s) => s.user!)
  const incidents = useReducer(IncidentStore.store, (s) => s.incidents)

  const manif = React.useMemo(() => findManifestation(manifestations, form.manifestationId), [manifestations, form])
  const tenant = React.useMemo(() => findTenant(tenants, form.locatairePermanentId), [tenants, form])

  const places = useReducer(LocationStore.store, (s) => s.places)
  const spaces = React.useMemo(() => places.filter((p) => !p.parentId && p.enabled), [places])

  const [status, setStatus] = React.useState<Status>('loading')

  // looking for existing form
  const previous = forms.find((f) => f.id === id)
  const edlType = previous ? previous.type : type

  // entries edl if exists
  const entries = EDLs.filter(
    (edl) =>
      (edl.manifestationId === form.manifestationId || edl.locatairePermanentId === form.locatairePermanentId) &&
      edl.type === EtatDesLieuxTypeEnum.ENTREE
  )

  // spaces associated to entries edls
  const entriesSpaces =
    form.type === EtatDesLieuxTypeEnum.SORTIE
      ? spaces.filter((sp) => !!entries.find((edl) => edl.espaceIds.includes(sp.id)))
      : []

  const entriesIncidents = React.useMemo(
    () =>
      entriesSpaces.reduce((acc, cur) => {
        const subs = [cur.id, ...findLocalisationSubLevels(places, cur).map((pl) => pl.id)]

        const entriesSpaceIncidents = incidents.filter(
          (inc) =>
            inc.localisationId &&
            subs.includes(inc.localisationId) &&
            !!entries.find((edl) => edl.incidentAccepteIds.includes(inc.id))
        )

        acc[cur.id] = entriesSpaceIncidents
        return acc
      }, {} as { [spaceId: string]: IncidentResponse[] }),
    [entriesSpaces, places, incidents, entries]
  )

  const eventSpaces = React.useMemo(
    () =>
      spaces.reduce(
        (acc, cur) => {
          const isIN =
            (!!manif && manif.espaceIds.includes(cur.id)) ||
            (!!tenant && !!tenant.tenant.locaux.find((l) => l.localisationId === cur.id))

          const inEntries = !!entriesSpaces.find((sp) => sp.id === cur.id)

          if (type === EtatDesLieuxTypeEnum.ENTREE || !inEntries) {
            if (isIN) {
              acc.in.push(cur)
            } else {
              acc.out.push(cur)
            }
          }

          return acc
        },
        { in: [], out: [] } as { [type in INSection]: LocalisationResponse[] }
      ),
    [spaces, manif, tenant, entriesSpaces]
  )

  const steps: Step[] = allStepsEDL(1, i18n)

  React.useEffect(() => {
    setInitialData(previous)
    setStatus('fetched')
  }, [])

  React.useEffect(() => {
    if (form && origin) {
      EDLStore.actions.setForm({ origin })
    }
  }, [!!form, origin])

  React.useEffect(() => {
    // check if closed incidents are required
    if (form.incidentIds) {
      const ids = incidents.map((i) => i.id)

      const formMissing = form.incidentIds.incidentIds.filter((id) => !ids.includes(id))

      const entriesMissing = entries
        .reduce((acc, edl) => [...acc, ...edl.incidentAccepteIds, ...edl.incidentRefuseIds], [] as string[])
        .filter((id) => !ids.includes(id))

      const missing = Array.from(new Set([...formMissing, ...entriesMissing]))

      if (missing.length > 0) {
        // download every missing incidents from opened
        Promise.all(missing.map(api.incident.get))
          .then((res) => IncidentStore.actions.setIncidents([...incidents, ...res]))
          .catch(Logger.error)
      } else {
        // no missing store result
        IncidentStore.actions.setIncidents(incidents)
      }
    }
  }, [form.incidentIds, incidents])

  React.useEffect(() => {
    if (form && form.id) {
      const prev = forms.find((f) => f.id === form.id)

      if (prev) {
        const updateForms = forms.map((f) => (f.id === form.id ? form : f))
        EDLStore.actions.setForms(updateForms)
      } else {
        EDLStore.actions.addForm(form)
      }
    }
  }, [form])

  const setInitialData = (prev?: EDLForm) => {
    if (!prev) {
      // new Edl
      const newForm: EDLForm = {
        id,
        manifestationId,
        locatairePermanentId: tenantId,
        type,
        utilisateurInitiateurId: user.id,
        espaceIds: [],
        espacesCloturesIds: [],
        incidentAcceptIds: [],
        incidentRefuseIds: [],
        incidentOrganisateurIds: [],
        incidentViparisIds: [],
        dateDeCreation: new Date().toISOString(),
        commentaire: undefined,
        utilisateurInitiateurSignature: undefined,
        contactOrganisateur: undefined,
        clientAbsent: false,
        rememberedSignature: false,
        incidentIds: undefined,
        newIds: [],
        tokenIncident: undefined,
        origin,
      }

      EDLStore.actions.setForm(newForm) // add to current form edition
    } else {
      EDLStore.actions.setForm(prev)
    }
  }

  const onSpaceRemoved = (ids: string[], prevForm: EDLForm): EDLForm => {
    if (!form.incidentIds) {
      return prevForm
    }

    const associatedIncs = filterIncidentsBySpace(places, incidents, form.incidentIds.incidentIds, ids).map((i) => i.id)

    const next: Partial<EDLForm> = associatedIncs
      ? {
          incidentAcceptIds: form.incidentAcceptIds.filter((item) => !associatedIncs.includes(item)),
          incidentRefuseIds: form.incidentRefuseIds.filter((item) => !associatedIncs.includes(item)),
          incidentOrganisateurIds: form.incidentOrganisateurIds.filter((item) => !associatedIncs.includes(item)),
          incidentViparisIds: form.incidentViparisIds.filter((item) => !associatedIncs.includes(item)),
          incidentIds: {
            token: form.incidentIds.token,
            incidentIds: form.incidentIds.incidentIds.filter((item) => !associatedIncs.includes(item)),
          },
        }
      : {}

    return {
      ...prevForm,
      ...next,
      espaceIds: form.espaceIds.filter((i) => !ids.includes(i)),
      espacesCloturesIds: form.espacesCloturesIds.filter((i) => !ids.includes(i)),
    }
  }

  const onSpaceAdded = (ids: string[], prevForm: EDLForm) => {
    setStatus('loading')

    api.edl
      .incidents({
        typeEtatDesLieux: edlType,
        espaceIds: ids,
        manifestationId: prevForm.manifestationId,
        locatairePermanentId: prevForm.locatairePermanentId,
      })
      .then((incToken) => {
        const nextToken: IncidentTokenResponse = {
          token: incToken.token,
          incidentIds: prevForm.incidentIds?.incidentIds
            ? Array.from(new Set([...prevForm.incidentIds.incidentIds, ...incToken.incidentIds]))
            : incToken.incidentIds,
        }
        const next: Partial<EDLForm> = {
          incidentIds: nextToken,
          incidentAcceptIds: Array.from(new Set([...prevForm.incidentAcceptIds, ...incToken.incidentIds])),
          incidentOrganisateurIds: Array.from(new Set([...prevForm.incidentOrganisateurIds, ...incToken.incidentIds])),
          espaceIds: [...prevForm.espaceIds, ...ids],
          espacesCloturesIds: [...prevForm.espacesCloturesIds],
          tokenIncident: nextToken.token,
        }
        EDLStore.actions.setForm(next)
      })
      .finally(() => setStatus('fetched'))

    Promise.all(ids.map((spaceId) => api.plan.get(spaceId)))
      .then((plans) => {
        plans.forEach((res) => {
          if (res.viewers && res.viewers.geojson && res.viewers.geojson.path) {
            PlanStore.actions.updatePlan(res)
            ensureCached(res.viewers.geojson.path)
          }
        })
      })
      .catch(Logger.error)
  }

  const onSelectNewPlaces = (pls: string[]) => {
    const toAdd = pls.filter((p) => !form.espaceIds.includes(p))
    const toRemove = form.espaceIds.filter((p) => !pls.includes(p))

    // remove every associated space incidents
    const removed = toRemove.length !== 0 ? onSpaceRemoved(toRemove, form) : form

    if (toAdd.length !== 0) {
      // add and validate every associated space incidents
      onSpaceAdded(toAdd, removed)
    } else {
      EDLStore.actions.setForm(removed)
    }

    Drawer.close()
  }

  const selectPlaces = () => {
    if (online) {
      Service.open(
        {
          title: i18n.t('screens.edl.spaces.modify'),
          subtitle: i18n.t('screens.edl.drawer.description'),
          onItemSelected: (np) => onSelectNewPlaces(np),
          sections:
            type === EtatDesLieuxTypeEnum.SORTIE && entriesSpaces.length > 0
              ? [
                  {
                    title: i18n.t('screens.edl.spaces.spacesEntry'),
                    data: entriesSpaces
                      .map((i) => ({ label: i.libelle, value: i.id }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                    selectAllEnabled: true,
                  },
                  {
                    title: i18n.t('screens.edl.spaces.otherSpacesManifestation'),
                    data: eventSpaces.in
                      .map((i) => ({ label: i.libelle, value: i.id }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  },
                  {
                    title: i18n.t('screens.edl.spaces.otherSpaces'),
                    data: eventSpaces.out
                      .map((i) => ({ label: i.libelle, value: i.id }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  },
                ].filter((s) => s.data.length > 0)
              : [
                  {
                    title: i18n.t('screens.edl.spaces.spacesManifestation'),
                    data: eventSpaces.in
                      .map((i) => ({ label: i.libelle, value: i.id }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                    selectAllEnabled: true,
                  },
                  {
                    title: i18n.t('screens.edl.spaces.otherSpaces'),
                    data: eventSpaces.out
                      .map((i) => ({ label: i.libelle, value: i.id }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                  },
                ].filter((s) => s.data.length > 0),
          value: form.espaceIds,
        },
        isPhone
      )
    } else {
      onOffline()
    }
  }

  const onPlaceIncidents = (spaceId: string) => {
    const next = form.espacesCloturesIds.filter((s) => s !== spaceId)
    EDLStore.actions.setForm({ espacesCloturesIds: next })

    navigation.push(`/edl/map/${id}?spaceId=${spaceId}`)
  }

  const onDelete = () => {
    SimpleAlert.open({
      title: i18n.t('screens.edl.spaces.alert.title'),
      description: i18n.t('screens.edl.spaces.alert.body'),
      buttons: [
        {
          label: i18n.t('screens.edl.spaces.alert.cancel'),
          onClick: () => SimpleAlert.close(),
          color: 'cancel',
        },
        {
          label: i18n.t('screens.edl.spaces.alert.delete'),
          onClick: () => {
            SimpleAlert.close()
            EDLStore.actions.resetForm()
            EDLStore.actions.removeForm(form.id)
            navigation.push(`/edl`)
          },
          color: 'default',
        },
      ],
    })
  }

  const onOffline = () => {
    SimpleAlert.open({
      title: i18n.t('screens.edl.spaces.alert.failed'),
      description: i18n.t('screens.edl.spaces.alert.offline'),
      buttons: [
        {
          label: i18n.t('common.ok'),
          onClick: () => SimpleAlert.close(),
        },
      ],
    })
  }

  const onNext = () => {
    if (online) {
      setStatus('loading')
      actions.sync()

      // update global token with request
      api.edl
        .incidents({
          typeEtatDesLieux: edlType,
          espaceIds: form.espaceIds,
          manifestationId: form.manifestationId,
          locatairePermanentId: form.locatairePermanentId,
        })
        .then((res) => {
          const toUpdate = res.incidentIds.filter(
            (i) => !form.incidentAcceptIds.includes(i) && !form.incidentRefuseIds.includes(i)
          )

          const nextToken: IncidentTokenResponse = {
            token: res.token,
            incidentIds: !!form.incidentIds
              ? Array.from(new Set([...form.incidentIds.incidentIds, ...res.incidentIds]))
              : res.incidentIds,
          }

          if (toUpdate.length === 0) {
            // all spaces are up to date

            const next: Partial<EDLForm> = {
              incidentIds: nextToken,
              incidentAcceptIds: form.incidentAcceptIds.filter((id) => res.incidentIds.includes(id)),
              incidentOrganisateurIds: form.incidentOrganisateurIds.filter((id) => res.incidentIds.includes(id)),
              incidentRefuseIds: form.incidentRefuseIds.filter((id) => res.incidentIds.includes(id)),
              incidentViparisIds: form.incidentViparisIds.filter((id) => res.incidentIds.includes(id)),
            }

            // update form
            EDLStore.actions.setForm(next)

            navigation.push(`/edl/recap/${form.id}`)
          } else {
            // get incidents data
            Promise.all(toUpdate.map(api.incident.get)).then((incs) => {
              // find associated spaces
              const locs = spaces.filter((s) => {
                const subs = [s, ...findLocalisationSubLevels(places, s)]
                return !!subs.find((sub) => incs.find((i) => sub.id === i.localisationId))
              })

              const next: Partial<EDLForm> = {
                incidentIds: nextToken,
                incidentAcceptIds: Array.from(new Set([...form.incidentAcceptIds, ...toUpdate])),
                incidentOrganisateurIds: Array.from(new Set([...form.incidentOrganisateurIds, ...toUpdate])),
                espacesCloturesIds: form.espacesCloturesIds.filter((ec) => !locs.find((l) => l.id === ec)),
                newIds: toUpdate,
              }

              // update form
              EDLStore.actions.setForm(next)

              // throw alert to ask for check new incidents
              SimpleAlert.open({
                title: i18n.t('screens.edl.spaces.alert.failed'),
                description: i18n.t('screens.edl.spaces.alert.complete'),
                buttons: [
                  {
                    label: i18n.t('common.ok'),
                    onClick: () => SimpleAlert.close(),
                  },
                ],
              })
            })
          }
          EDLStore.actions.setForm({ tokenIncident: res.token })
        })
        .finally(() => setStatus('fetched'))
    } else {
      // ask for user to connect and start sync
      onOffline()
    }
  }

  const renderBottomButton = () => {
    const spacesAllClosed = form.espacesCloturesIds.length === form.espaceIds.length

    if (!isPhone) {
      return (
        <BottomButtonWeb>
          <ButtonWeb>
            <Button
              color="light"
              label={i18n.t('screens.edl.spaces.back')}
              onClick={() =>
                navigation.jumpTo(form.origin === 'manifestation' ? `/manifestation/${form.manifestationId}` : '/edl')
              }
              status="active"
            />
          </ButtonWeb>
          <ButtonWeb>
            <Button
              color="default"
              label={i18n.t('screens.edl.nextStep')}
              onClick={onNext}
              status={form.espaceIds.length > 0 && spacesAllClosed ? 'active' : 'disabled'}
            />
          </ButtonWeb>
        </BottomButtonWeb>
      )
    }
    return (
      <>
        <BottomButton width={width} isFirst>
          <Button
            color="light"
            label={i18n.t('screens.edl.spaces.modify')}
            onClick={selectPlaces}
            status={!!manif || !!tenant ? 'active' : 'disabled'}
          />
        </BottomButton>
        <BottomButton width={width}>
          <Button
            color="default"
            label={i18n.t('screens.edl.nextStep')}
            onClick={onNext}
            status={form.espaceIds.length > 0 && spacesAllClosed ? 'active' : 'disabled'}
          />
        </BottomButton>
      </>
    )
  }

  const renderBottomHeader = () => {
    const dates = !!tenant ? getTenantDates(tenant.tenant) : undefined
    const status = !!dates ? getTenantStatus(dates) : undefined

    return (
      <ManifestationDateContainer>
        {!!form.manifestationId && !!manif ? (
          <ManifestationDate>{manifestationDate(manif, i18n)}</ManifestationDate>
        ) : !!form.locatairePermanentId && !!dates && !!status ? (
          <ManifestationDate>
            {i18n.t(`screens.tenants.${status}Date`, { date: status === 'former' ? dates.end : dates.start })}
          </ManifestationDate>
        ) : undefined}
      </ManifestationDateContainer>
    )
  }

  const renderGroupedPlace = ({ item }: { item: LocalisationResponse }) => {
    const spaceInc =
      form.incidentIds && filterIncidentsBySpace(places, incidents, form.incidentIds.incidentIds, [item.id])
    const spaceIncidentIds = spaceInc || []

    const accepted = spaceIncidentIds.filter((i) => form.incidentAcceptIds.includes(i.id))
    const isClosed = form.espacesCloturesIds.includes(item.id)

    return (
      <PlaceContainer key={item.libelle} activeOpacity={0.8} onPress={() => onPlaceIncidents(item.id)}>
        <PlaceContent>
          <InformationsContainer>
            <PlaceNameContainer>
              <PlaceName>{item.libelle}</PlaceName>
            </PlaceNameContainer>

            {isClosed && (
              <ClosedContainer>
                <ClosedSpace>{i18n.t('screens.edl.spaces.closed')}</ClosedSpace>
                <Icon name="check" color={theme.colors.primary} size={24} />
              </ClosedContainer>
            )}
          </InformationsContainer>
          <IncidentContainer>
            <IncidentNumber>{accepted.length}</IncidentNumber>
            <IncidentsText>{i18n.t('screens.edl.spaces.incidents')}</IncidentsText>
          </IncidentContainer>
        </PlaceContent>
        <DeleteContainer activeOpacity={0.9} onPress={() => onDeleteSpace(item.id)}>
          <Icon name="cancel" size={20} color={theme.colors.primary} />
        </DeleteContainer>
      </PlaceContainer>
    )
  }

  const onDeleteSpace = (spaceId: string) =>
    SimpleAlert.open({
      title: i18n.t('screens.edl.spaces.alert.deleteSpace'),
      description: i18n.t('screens.edl.spaces.alert.deleteSpaceDescription'),
      buttons: [
        { label: i18n.t('common.cancel'), color: 'cancel', onClick: () => SimpleAlert.close() },
        {
          label: i18n.t('common.confirm'),
          onClick: () => {
            EDLStore.actions.setForm(onSpaceRemoved([spaceId], form))
            SimpleAlert.close()
          },
        },
      ],
    })

  const renderPlace = ({ item }: { item: LocalisationResponse }) => {
    const entriesSpaceIncidents = entriesIncidents[item.id]
    const spaceInc =
      form.incidentIds && filterIncidentsBySpace(places, incidents, form.incidentIds.incidentIds, [item.id])
    const spaceIncidentIds = spaceInc || []

    const accepted = spaceIncidentIds.filter((i) => form.incidentAcceptIds.includes(i.id))
    const isClosed = form.espacesCloturesIds.includes(item.id)

    return (
      <PlaceContainer key={item.libelle} activeOpacity={0.8} disabled onPress={() => onPlaceIncidents(item.id)}>
        <PlaceContent>
          <InformationsContainer>
            <PlaceNameContainer>
              <PlaceName>{item.libelle}</PlaceName>
            </PlaceNameContainer>

            {type === EtatDesLieuxTypeEnum.SORTIE && (
              <TagContainer>
                <TagIncident>
                  <IncidentNumber>{entriesSpaceIncidents ? entriesSpaceIncidents.length : 0}</IncidentNumber>
                </TagIncident>
                <IncidentsText>{i18n.t(`screens.edl.entryIncidents`)}</IncidentsText>
              </TagContainer>
            )}

            <TagContainer>
              <TagIncident>
                <IncidentNumber>{accepted.length}</IncidentNumber>
              </TagIncident>
              <IncidentsText>
                {i18n.t(`screens.edl.${type === EtatDesLieuxTypeEnum.ENTREE ? 'incident' : 'newIncidents'}`)}
              </IncidentsText>
            </TagContainer>

            {bp === 'big' && (
              <ClosedContainer>
                {isClosed && (
                  <>
                    <ClosedSpace>{i18n.t('screens.edl.spaces.closed')}</ClosedSpace>
                    <Icon name="check" color={theme.colors.primary} size={24} />
                  </>
                )}
              </ClosedContainer>
            )}
          </InformationsContainer>

          <ButtonContainer>
            <Button
              color="light"
              label={i18n.t('screens.edl.spaces.seeSpace')}
              onClick={() => onPlaceIncidents(item.id)}
              status="active"
            />
          </ButtonContainer>
        </PlaceContent>

        {bp !== 'big' && isClosed && (
          <ClosedContainer>
            <ClosedSpace>{i18n.t('screens.edl.spaces.closed')}</ClosedSpace>
            <Icon name="check" color={theme.colors.primary} size={24} />
          </ClosedContainer>
        )}

        <DeleteContainer activeOpacity={0.9} onPress={() => onDeleteSpace(item.id)}>
          <Icon name="cancel" size={24} color={theme.colors.primary} />
        </DeleteContainer>
      </PlaceContainer>
    )
  }

  const name = manif ? manif.libelle : tenant ? tenant.tenant.nomLocatairePermanent : ''

  const headerLinks = [
    { label: i18n.t(`screens.site.sites.${site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t(`screens.edl.title`), onClick: () => navigation.push(`/edl`) },
  ]

  const headerButtons: HeaderButton[] = [
    {
      label: i18n.t('screens.edl.delete'),
      onClick: onDelete,
    },
  ]

  return (
    <Root
      renderFixedBottom={renderBottomButton}
      loading={status === 'loading'}
      onLeft={navigation.goBack}
      right="delete"
      onRight={onDelete}
      header={i18n.t('screens.edl.title')}
      renderSubHeader={() => <Steps steps={steps} />}
      fixed>
      <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        {status === 'fetched' ? (
          <>
            {!isPhone && (
              <Header
                title={i18n.t('screens.edl.subTitle', {
                  manifestationName: name,
                  type: i18n.t(`screens.edlRecap.${type}`),
                })}
                links={headerLinks}
                buttons={headerButtons}
                renderBottom={renderBottomHeader}
              />
            )}
            <MainContainer>
              {!isPhone ? (
                <SectionTitle
                  title={i18n.t('screens.edl.spaces.title')}
                  button={{
                    label: i18n.t('screens.edl.spaces.modify'),
                    onClick: () => selectPlaces(),
                  }}
                />
              ) : (
                <>
                  <Title>{i18n.t('screens.edl.spaces.title')}</Title>
                  <Title top={4}>{i18n.t('screens.edl.spaces.description')}</Title>
                </>
              )}

              {form.espacesCloturesIds.length !== form.espaceIds.length && (
                <CloseSpacesContainer>
                  <TouchableOpacity
                    activeOpacity={0.9}
                    onPress={() =>
                      ModalListService.open(
                        {
                          data: spaces
                            .filter((s) => form.espaceIds.includes(s.id) && !form.espacesCloturesIds.includes(s.id))
                            .map((s) => ({ value: s.id, label: s.libelle })),
                          onItemSelected: (items) => EDLStore.actions.setForm({ espacesCloturesIds: items }),
                          title: i18n.t('screens.edl.spaces.closeSpaces'),
                          value: form.espacesCloturesIds,
                          subtitle: '',
                          selectAllEnabled: true,
                        },
                        isPhone
                      )
                    }>
                    <CloseSpaces>{i18n.t('screens.edl.spaces.closeSpaces')}</CloseSpaces>
                  </TouchableOpacity>
                </CloseSpacesContainer>
              )}

              {form.espaceIds.length > 0 ? (
                <FlatList
                  data={spaces
                    .filter((s) => form.espaceIds.includes(s.id))
                    .sort((a, b) => a.libelle.localeCompare(b.libelle))}
                  renderItem={bp === 'phone' ? renderGroupedPlace : renderPlace}
                />
              ) : (
                <EmptyContainer>
                  <Provider>{i18n.t('screens.edl.spaces.emptySpaces')}</Provider>
                </EmptyContainer>
              )}
            </MainContainer>
          </>
        ) : (
          <MainContainer>
            <MissingInfo>{i18n.t('common.error')}</MissingInfo>
          </MainContainer>
        )}
      </ScrollContainer>
    </Root>
  )
}

export default EDLSpaceScreen

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
`

const MainContainer = styled(View)`
  display: flex;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.backgroundSecondary : props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '24px 16px 0px' : '24px')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex-wrap: wrap;'}
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const ManifestationDate = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  margin-top: 8px;
`

const ManifestationDateContainer = styled(View)`
  padding-bottom: 16px;
`

const PlaceContainer = styled(TouchableOpacity)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? '16px' : '44px 24px')};
  border-radius: 8px;
  margin: 12px 4px;
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.constants.shadow};
`

const PlaceContent = styled(View)`
  flex-direction: row;
  align-items: center;
`

const PlaceName = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const PlaceNameContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1;'};
`

const IncidentsText = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption2 : props.theme.fonts.Body)};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primary};
`

const IncidentNumber = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primaryText : props.theme.colors.background};
`

const TagIncident = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: 30px;
  height: 32px;
  width: 32px;
  align-items: center;
  justify-content: center;
  margin: 0 13px;
`

const TagContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  flex: 1;
`

const IncidentContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.cardSecondary};
  padding: 4px;
  justify-content: center;
  align-items: center;
`

const ButtonContainer = styled(View)`
  margin-left: 16px;
  width: ${(props) => (props.theme.breakpoint === 'medium' ? '190' : '230')}px;
`

const InformationsContainer = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  ${(props) => props.theme.breakpoint !== 'phone' && 'align-items: center;'};
  flex: 1;
`

const BottomButton = styled(View)<{ width: number; isFirst?: boolean }>`
  width: ${(props) => props.width}px;
  background-color: ${(props) => props.theme.colors.background}E6;
  padding: ${(props) => (props.isFirst ? '16px 16px 8px' : '8px 16px 16px')};
`

const BottomButtonWeb = styled(View)`
  justify-content: center;
  align-items: center;
  flex-direction: row;
`

const ButtonWeb = styled(View)`
  border: 0 solid ${(props) => props.theme.colors.border};
  border-top-width: 1px;
  width: ${(props) => (props.theme.breakpoint === 'medium' ? '200' : '300')}px;
  padding: 18px 48px;
`

const ClosedSpace = styled(Text)`
  ${(props) => props.theme.fonts.Callout};
  color: ${(props) => props.theme.colors.primary};
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin: 12px 0px;'};
  margin-right: 12px;
`

const ClosedContainer = styled(View)`
  ${(props) => props.theme.breakpoint === 'big' && 'flex: 1;'};
  ${(props) => props.theme.breakpoint === 'big' && 'padding-left: 12px;'};
  flex-direction: row;
  align-items: center;
`

const EmptyContainer = styled(View)`
  margin-top: 16px;
  justify-content: center;
`

const Provider = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const Title = styled(Text)<{ top?: number; center?: boolean }>`
  ${(props) => (props.top ? props.theme.fonts.Caption2 : props.theme.fonts.Headline)};
  ${(props) => props.top && `color: ${props.theme.colors.secondaryText};`}
  ${(props) => props.top && `margin-top: ${props.top}px;`}
  ${(props) => props.top && `margin-bottom: 8px;`}
  ${(props) => props.center && 'text-align: center;'}
`

const DeleteContainer = styled(TouchableOpacity)`
  position: absolute;
  top: ${(props) => (props.theme.breakpoint === 'phone' ? -11 : 0)}px;
  right: ${(props) => (props.theme.breakpoint === 'phone' ? -11 : 0)}px;
  padding: 5px;
`

const CloseSpacesContainer = styled(View)`
  flex-direction: row;
  justify-content: flex-end;
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? '0px 0px 8px' : '24px 0px 12px')};
`

const CloseSpaces = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  text-decoration: underline;
  color: ${(props) => props.theme.colors.primary};
  text-decoration-color: ${(props) => props.theme.colors.primary};
`
